import { useState } from 'react'
import { FiX, FiMail, FiAlertCircle } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function SendNoteModal({ isOpen, onClose, note }) {
  const [recipients, setRecipients] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Parse and validate emails
    const emailList = recipients
      .split(/[,\n]+/)
      .map(email => email.trim())
      .filter(email => email)

    if (emailList.length === 0) {
      setError('Please enter at least one email address')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const invalidEmails = emailList.filter(email => !emailRegex.test(email))
    if (invalidEmails.length > 0) {
      setError(`Invalid email format: ${invalidEmails.join(', ')}`)
      return
    }

    try {
      setLoading(true)
      const response = await axios.post(`/notes/${note._id}/send`, {
        recipientEmails: emailList,
        customMessage: customMessage.trim() || null
      })

      // Show success message
      const successMsg = `✅ Email sent immediately to ${response.data.results.successCount} recipient(s)`
      toast.success(successMsg)

      // Show scheduled send info if applicable
      if (response.data.results.scheduledSendResults?.scheduled) {
        const scheduledMsg = `📅 ${response.data.results.scheduledSendResults.message}`
        toast.success(scheduledMsg, { duration: 5000 })
      }

      if (response.data.results.failureCount > 0) {
        toast.error(`Failed to send to ${response.data.results.failureCount} recipient(s)`)
      }

      // Reset form and close
      setRecipients('')
      setCustomMessage('')
      setError('')
      onClose()
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to send note'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !note) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiMail className="text-blue-600 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Share Note</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
            disabled={loading}
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Note Title Display */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-gray-600 mb-1">Note:</p>
          <p className="text-gray-800 font-medium truncate">{note.title}</p>
        </div>

        {/* Scheduled Send Info (if applicable) */}
        {note.scheduledUTC && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-700 font-semibold mb-2">⏰ Scheduled Send</p>
            <div className="text-xs text-blue-600 space-y-1">
              <p>✅ Note will be sent <strong>immediately</strong> to all recipients</p>
              <p>📅 Note will be sent again on <strong>{note.scheduleDate}</strong> at <strong>00:00</strong> ({note.timezone})</p>
              <p className="italic">All recipients will receive the note twice:</p>
              <ul className="list-disc list-inside ml-1 mt-1">
                <li>Now (when you click Send)</li>
                <li>Again on the scheduled date at start of day</li>
              </ul>
            </div>
          </div>
        )}

        {!note.scheduledUTC && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-600 font-medium">ℹ️ Note Info</p>
            <p className="text-xs text-gray-600 mt-1">This note has no scheduled date, so it will only be sent once (now)</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Recipients Field */}
          <div className="mb-4">
            <label htmlFor="recipients" className="block text-sm font-medium text-gray-700 mb-2">
              Email Recipient(s) <span className="text-red-500">*</span>
            </label>
            <textarea
              id="recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="Enter email addresses (comma or line separated)&#10;user@example.com&#10;another@example.com"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
              rows="3"
              disabled={loading}
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2">Separate multiple emails with commas or newlines</p>
          </div>

          {/* Custom Message Field */}
          <div className="mb-4">
            <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 mb-2">
              Custom Message <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a personal message to include in the email..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none"
              rows="3"
              disabled={loading}
              maxLength="500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {customMessage.length}/500 characters
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-3">
              <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading || recipients.trim() === ''}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <FiMail />
                  Send
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
